import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { DamageReason } from 'src/types/damage-reason';
import {
  IPackagingDamageDispatchDetailedItem,
  IPackagingDamageDispatchListItem,
} from 'src/types/packaging-damage-dispatch';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  damageDispatches: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingDamageDispatchListItem[];
  };
  damageDispatch: IPackagingDamageDispatchDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  damageDispatches: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  damageDispatch: null,
};

const packagingDamageDispatchSlice = createSlice({
  name: 'packagingDamageDispatch',
  initialState,
  reducers: {
    removePackagingDamageDispatch: (state, action) => {
      const { damageDispatchId } = action.payload;

      const index = state.damageDispatches.items.findIndex(
        (item) => item._id === damageDispatchId
      );

      if (index > -1) {
        action.payload.index = index;
        state.damageDispatches.items = state.damageDispatches.items.filter(
          (item) => item._id !== damageDispatchId
        );
      }
    },
    rollbackPackagingDamageDispatchDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.damageDispatches.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingDamageDispatches.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingDamageDispatches.fulfilled, (state, action) => {
        state.status = 'success';
        state.damageDispatches = action.payload;
      })
      .addCase(getPackagingDamageDispatches.rejected, (state, action) => {
        state.damageDispatches = initialState.damageDispatches;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingDamageDispatchById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingDamageDispatchById.fulfilled, (state, action) => {
        state.status = 'success';
        state.damageDispatch = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingDamageDispatchById.rejected, (state, action) => {
        state.damageDispatch = initialState.damageDispatch;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingDamageDispatchSlice.reducer;

export const {
  removePackagingDamageDispatch,
  rollbackPackagingDamageDispatchDeletion,
} = packagingDamageDispatchSlice.actions;

export const getPackagingDamageDispatches = createAsyncThunk(
  'packagingDamageDispatch/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `packaging/damage-dispatch?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingDamageDispatchById = createAsyncThunk(
  'packagingDamageDispatch/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/damage-dispatch/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingDamageDispatch = createAsyncThunk(
  'packagingDamageDispatch/delete',
  async (
    params: { item: IPackagingDamageDispatchListItem; index: number },
    { dispatch }
  ) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(
        `packaging/damage-dispatch/${item._id}`
      );
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingDamageDispatchDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TPackagingDamageDispatchItemInput {
  packagingItem: string;
  quantity: number;
  damageReason: DamageReason;
}

export interface TCreatePackagingDamageDispatch {
  damageDate: Date;
  remarks: string | null;
  items: TPackagingDamageDispatchItemInput[];
}

export const createPackagingDamageDispatch = createAsyncThunk(
  'packagingDamageDispatch/create',
  async (params: { damageDispatchTransaction: TCreatePackagingDamageDispatch }) => {
    const { damageDispatchTransaction } = params;
    const response = await axios.post(
      'packaging/damage-dispatch',
      damageDispatchTransaction
    );
    return response.data.data;
  }
);
